import { recommendationFromStats, type GuestStatsSample } from '@playcastdotio/PeerCore';
import type { WebrtcQualityStatsMetric } from '../types/webrtcTypes';

/**
 * Bridges PeerCore guest samples to the existing metrics pipeline.
 *
 * Client WebRTC telemetry and quality-mode feedback previously existed only in
 * `peerLegacy.ts`, behind `USE_NEW_PEER === false` — and config sets it `true`. So the
 * shipping stack emitted nothing at all: no stats reached realtime-api, and no framerate
 * panic reached the host. The legacy path cannot simply be re-enabled because it is built
 * on `libs/Peer`, which is deprecated and frozen (ARCHITECTURE.md §Deprecated / Frozen Code),
 * so the behaviour is rebuilt here on top of the PeerCore sampler.
 *
 * Batching mirrors the path it replaces (peerLegacy.ts:188-206): flush every
 * GUEST_STATS_BATCH_SIZE samples, and flush whatever is buffered when the session ends so a
 * short session still reports.
 */

/** Samples per batch. At the sampler's 1 Hz cadence this is one message per ~10s. */
export const GUEST_STATS_BATCH_SIZE = 10;
/** Consecutive `panic` recommendations before a hard reset is requested. */
export const GUEST_PANIC_HARD_RESET_THRESHOLD = 3;

export type GuestStatsBridgeDeps = {
  /** Existing realtime metric sender. */
  sendBatch: (stats: WebrtcQualityStatsMetric[], collectionIntervalMs: number) => void;
  /** Existing analytics event. */
  sendAnalytics: (stats: WebrtcQualityStatsMetric[], collectionIntervalMs: number) => void;
  /** Datachannel send to the host, for frameratePanic. */
  sendToHost: (hardReset: boolean) => void;
  /** Framerate-panic analytics event. */
  sendPanicEvent: (hardReset: boolean, reason: 'fps' | 'bitrate') => void;
};

/** Interval the batch represents, in ms. Matches the sampler's poll cadence. */
const COLLECTION_INTERVAL_MS = 1_000;

export class GuestStatsBridge {
  private buffer: WebrtcQualityStatsMetric[] = [];
  private consecutivePanics = 0;

  constructor(private readonly deps: GuestStatsBridgeDeps) {}

  /** Feed one sample from the live guest session. */
  public onSample(sample: GuestStatsSample): void {
    const { recommendation, reason } = recommendationFromStats(sample.raw);
    this.buffer.push({ recommendation, reason, rawStats: sample.raw });

    if (this.buffer.length >= GUEST_STATS_BATCH_SIZE) {
      this.flush();
    }

    this.handleRecommendation(recommendation, reason);
  }

  /** Flush any buffered samples immediately (session end / disconnect). */
  public flush(): void {
    if (this.buffer.length === 0) return;
    const batch = this.buffer;
    this.buffer = [];
    // A failing sender must never propagate into the peer lifecycle.
    try {
      this.deps.sendAnalytics(batch, COLLECTION_INTERVAL_MS);
    } catch (error) {
      console.warn('[GuestStatsBridge] analytics send failed', error);
    }
    try {
      this.deps.sendBatch(batch, COLLECTION_INTERVAL_MS);
    } catch (error) {
      console.warn('[GuestStatsBridge] metric send failed', error);
    }
  }

  /** Drop buffered state without sending — used when starting a new session. */
  public reset(): void {
    this.buffer = [];
    this.consecutivePanics = 0;
  }

  private handleRecommendation(recommendation: string, reason: string): void {
    if (recommendation !== 'panic') {
      this.consecutivePanics = 0;
      return;
    }

    this.consecutivePanics += 1;
    const hardReset = this.consecutivePanics > GUEST_PANIC_HARD_RESET_THRESHOLD;
    // Send on TRANSITIONS only — streak start and hard-reset escalation — not on every
    // 1Hz sample of a persisting panic. A sustained panic used to emit a message per
    // second, which is noise for the host and the analytics pipeline alike.
    const isStreakStart = this.consecutivePanics === 1;
    if (!isStreakStart && !hardReset) return;
    const panicReason: 'fps' | 'bitrate' = reason === 'BITRATE_PANIC' ? 'bitrate' : 'fps';

    try {
      this.deps.sendToHost(hardReset);
    } catch (error) {
      console.warn('[GuestStatsBridge] frameratePanic send failed', error);
    }
    try {
      this.deps.sendPanicEvent(hardReset, panicReason);
    } catch (error) {
      console.warn('[GuestStatsBridge] panic analytics failed', error);
    }

    if (hardReset) {
      // Start counting again so a persistent problem re-escalates rather than requesting a
      // hard reset on every subsequent sample.
      this.consecutivePanics = 0;
    }
  }
}
